import { z } from 'zod'
import { displayNameSchema, emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'

/**
 * How an account proves who it is. `oidc` and `password` are both
 * production paths; `dev_password` exists only so the platform can be
 * developed without an external IdP and is rejected outside the local
 * environment.
 */
export const AUTH_PROVIDER_VALUES = ['oidc', 'dev_password', 'password'] as const
export const authProviderIdSchema = z.enum(AUTH_PROVIDER_VALUES)
export type AuthProviderId = z.infer<typeof authProviderIdSchema>

export const authIdentitySchema = z.object({
  id: publicIdSchema,
  userId: publicIdSchema,
  provider: authProviderIdSchema,
  /** Stable subject from the provider (`sub` for OIDC, the email for dev). */
  providerSubject: z.string().min(1).max(255),
  email: emailSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
  lastUsedAt: utcTimestampSchema.nullable(),
})

export type AuthIdentity = z.infer<typeof authIdentitySchema>

/**
 * The normalised profile an `AuthProvider` hands back after a successful
 * callback. It is provider-agnostic on purpose: nothing downstream should have
 * to know which IdP was used.
 */
export const authenticatedProfileSchema = z.object({
  provider: authProviderIdSchema,
  providerSubject: z.string().min(1).max(255),
  email: emailSchema,
  displayName: displayNameSchema,
  avatarUrl: z.url().max(2048).nullable(),
  emailVerified: z.boolean(),
})

export type AuthenticatedProfile = z.infer<typeof authenticatedProfileSchema>

export const devPasswordLoginRequestSchema = z.strictObject({
  email: emailSchema,
  password: z.string().min(8).max(200),
})

export type DevPasswordLoginRequest = z.infer<typeof devPasswordLoginRequestSchema>

export const oidcCallbackQuerySchema = z.object({
  code: z.string().min(1).max(2048),
  state: z.string().min(1).max(512),
})

export type OidcCallbackQuery = z.infer<typeof oidcCallbackQuerySchema>

// ---------------------------------------------------------------------------
// Production password authentication (Phase 2b). Distinct routes and schemas
// from the local-only `dev_password` adapter above — see
// apps/api/src/auth/production-password-provider.ts.
// ---------------------------------------------------------------------------

/**
 * NIST 800-63B-style length-based policy: a floor on length, no forced
 * character-class mix, no server-side maximum below what bcrypt-style KDFs
 * can hash without truncation concerns.
 */
export const PASSWORD_MIN_LENGTH = 10
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(200)
const turnstileTokenSchema = z.string().trim().min(1).max(4096).optional()

export const registerRequestSchema = z.strictObject({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
  turnstileToken: turnstileTokenSchema,
})
export type RegisterRequest = z.infer<typeof registerRequestSchema>

export const verifyEmailQuerySchema = z.object({
  id: publicIdSchema,
  token: z.string().min(1).max(512),
})
export type VerifyEmailQuery = z.infer<typeof verifyEmailQuerySchema>

export const passwordLoginRequestSchema = z.strictObject({
  email: emailSchema,
  password: z.string().min(1).max(200),
  turnstileToken: turnstileTokenSchema,
})
export type PasswordLoginRequest = z.infer<typeof passwordLoginRequestSchema>

export const passwordResetRequestSchema = z.strictObject({
  email: emailSchema,
  turnstileToken: turnstileTokenSchema,
})
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>

export const passwordResetConfirmSchema = z.strictObject({
  id: publicIdSchema,
  token: z.string().min(1).max(512),
  newPassword: passwordSchema,
})
export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>

// ---------------------------------------------------------------------------
// Judge Temporary Initial Password — forced first-login change (Wave R,
// API-051, BR-083). No `currentPassword` field: the caller reached this
// route on a session that was itself just minted by successfully
// authenticating with the Temporary Initial Password, which is the
// "equivalent fresh auth" the change-password rule asks for.
// ---------------------------------------------------------------------------

export const completeForcedPasswordChangeRequestSchema = z.strictObject({
  newPassword: passwordSchema,
})
export type CompleteForcedPasswordChangeRequest = z.infer<
  typeof completeForcedPasswordChangeRequestSchema
>

// ---------------------------------------------------------------------------
// Initial Super Admin Bootstrap (Wave N, PR-024). See
// docs/sdd/08-platform-governance-workflows.md.
// ---------------------------------------------------------------------------

export const bootstrapSuperAdminRequestSchema = z.strictObject({
  /** The deployment-level one-time credential (`BOOTSTRAP_ADMIN_SECRET`). Never logged. */
  credential: z.string().min(1).max(512),
})
export type BootstrapSuperAdminRequest = z.infer<typeof bootstrapSuperAdminRequestSchema>
